import { routeInfo } from './../../routeInfo';
import { IBaseAction } from './../base';
import { IHighlightLineParams, IUpdateScriptTextInEditorParams, IChangeEditorFontSizeParams, IChangeEditorThemeParams, IOpenErrorsParams, IShowSearchParams, ISetSearchTermParams, ISetReplaceTermParams, ISetSearchMetaParams } from './actions.scriptEditor';
import { put, call, takeEvery, all } from 'redux-saga/effects';
import { push, replace } from 'react-router-redux';
import * as types from './actionTypes.scriptEditor';
import * as errorSaga from '../errors/saga.errors';
import * as _ from 'lodash';
import scriptDraftsService from '../../services/ScriptDraftService';
import ToasterService from '../../services/ToasterService';
import globalEvents from '../../const/globalEvents';
import isLocalStorageAvailable from '../../services/mini/isLocalStorageAvailable';
import { CODE_THEME_STOTAGE_KEY, FONT_MODE_STOTAGE_KEY } from '../../const/storageKeys';
import store from '../../store';
import { SCRIPT_EDITOR } from '../../const/editorNames';
import { TEXT_TO_SHOW_LENGTH } from '../../views/pages/project/ScriptArea/ScriptArea';
import { CHARACTER_LIMIT } from '../../const/limits';
import { isScriptType } from '../../models/IScript';

declare const window: Window &
    typeof globalThis & {
        alanUi: any
    }

export function* watch() {
    yield takeEvery<IHighlightLineParams & IBaseAction>(types.HIGHLIGHT_LINE_REQ, highlightLine);
    yield takeEvery<IUpdateScriptTextInEditorParams & IBaseAction>(types.UPDATE_SCRIPT_TEXT_IN_EDITOR, updateScriptText);
    yield takeEvery<IOpenErrorsParams & IBaseAction>(types.OPEN_ERRORS_IN_ERROR_PANEL_REQ, openErrorsInErrorPanel);
    yield takeEvery<IChangeEditorFontSizeParams & IBaseAction>(types.CHANGE_EDITOR_FONT_SIZE_REQ, changeFontSize);
    yield takeEvery<IChangeEditorThemeParams & IBaseAction>(types.CHANGE_EDITOR_THEME_REQ, changeEditorTheme);
    yield takeEvery<IShowSearchParams & IBaseAction>(types.SCRIPT_SEARCH_SHOW_REQ, showSearch);
    yield takeEvery<IBaseAction>(types.SCRIPT_SEARCH_UPDATE_REQ, searchUpdate);
    yield takeEvery<ISetSearchTermParams & IBaseAction>(types.SCRIPT_SEARCH_SEARCH_TERM_REQ, setSearchTerm);
    yield takeEvery<ISetReplaceTermParams & IBaseAction>(types.SCRIPT_SEARCH_REPLACE_TERM_REQ, setReplaceTerm);
    yield takeEvery<ISetSearchMetaParams & IBaseAction>(types.SCRIPT_SEARCH_SET_META_REQ, setSearchMeta);
}

export function* highlightLine({ project, scriptName, lineNumber, lineType, character, lineOffset, scriptsHash, definitionName }: IHighlightLineParams) {
    try {
        const neededScript = _.find(project.scripts, { name: scriptName });
        if (neededScript) {
            const realScriptText = yield call(scriptDraftsService.getScriptText, neededScript);
            let lineCount;
            if (realScriptText) {
                lineCount = realScriptText.split('\n').length;
                if (lineCount < lineNumber) {
                    ToasterService.showError({ title: `Line <b>${lineNumber}</b> no longer exists in the script <b>${scriptName}</b>` });
                } else {
                    yield put(replace(routeInfo.projectScripts.url(project.name, scriptName)));
                    yield new Promise<void>((resolve) => {
                        setTimeout(() => {
                            resolve();
                        }, 0);
                    });
                    yield put({ type: types.HIGHLIGHT_LINE_OK, lineNumber, lineType, character, lineOffset, definitionName });
                    if (scriptsHash && project.scriptsHash !== scriptsHash) {
                        ToasterService.showWarning({
                            title: 'The script has changed since this interaction was made. Opening the current script version.'
                        });
                    }
                }
            }
        } else {
            ToasterService.showError({ title: `Script <b>${scriptName}</b> no longer exists in the project` });
        }
    } catch (error) {
        yield errorSaga.putError(error);
    }
}

export function* updateScriptText({ script, newScriptText }: IUpdateScriptTextInEditorParams) {
    try {
        if (newScriptText !== script.text) {
            yield call(scriptDraftsService.saveScriptDraft, script.scriptId, newScriptText);
        } else {
            yield call(scriptDraftsService.removeDraft, script.scriptId);
        }
        sendUpdateToProjectDetailsPanel();
    } catch (error) {
        yield errorSaga.putError(error);
    }
}

export interface IOpenErrorsEvent {
    detail: { filterName: 'runtimeErrors' | 'syntaxErrors' };
}

export function* openErrorsInErrorPanel({ shouldScrollToFirstError }: IOpenErrorsParams) {
    try {
        const project = store.getState().projectsInfo.currentProject;
        const syntaxErrors = store.getState().codeMetaData.syntaxErrors;
        if (syntaxErrors?.length > 0) {
            if (shouldScrollToFirstError) {
                const firstErr = syntaxErrors[0];
                yield highlightLine({ project, scriptName: firstErr.file, lineNumber: firstErr.line, character: firstErr.character });
            }
            window.dispatchEvent(new CustomEvent(globalEvents.OPEN_ERRORS, { detail: { filterName: 'syntaxErrors' } } as IOpenErrorsEvent));
        } else {
            window.dispatchEvent(new CustomEvent(globalEvents.OPEN_ERRORS, { detail: { filterName: 'runtimeErrors' } } as IOpenErrorsEvent));
        }
    } catch (error) {
        yield errorSaga.putError(error);
    }
}

export function* changeFontSize({ fontSize }: IChangeEditorFontSizeParams) {
    try {
        if (isLocalStorageAvailable()) {
            localStorage.setItem(FONT_MODE_STOTAGE_KEY, fontSize.toString());
        }
        yield put({ type: types.CHANGE_EDITOR_FONT_SIZE_OK, fontSize });
    } catch (error) {
        yield errorSaga.putError(error);
    }
}

export function* changeEditorTheme({ codeTheme }: IChangeEditorThemeParams) {
    try {
        if (isLocalStorageAvailable()) {
            localStorage.setItem(CODE_THEME_STOTAGE_KEY, codeTheme);
        }
        yield put({ type: types.CHANGE_EDITOR_THEME_OK, codeTheme });
    } catch (error) {
        yield errorSaga.putError(error);
    }
}

export function* showSearch({ searchKey, showSearch, showReplace }: IShowSearchParams) {
    try {
        yield put({ type: types.SCRIPT_SEARCH_SHOW_OK, searchKey, showSearch, showReplace });
    } catch (error) {
        yield errorSaga.putError(error);
    }
}

export function* searchUpdate() {
    try {
        yield put({ type: types.SCRIPT_SEARCH_UPDATE_OK, lastSearchUpdate: Date.now() });
    } catch (error) {
        yield errorSaga.putError(error);
    }
}

export function* setSearchTerm({ searchKey, searchTerm }: ISetSearchTermParams) {
    try {

        let statsScriptsSearch = {};

        if (searchKey === SCRIPT_EDITOR && searchTerm?.length > 0) {
            const curProject = store.getState().projectsInfo?.currentProject;
            const scripts = curProject?.scripts?.filter(script => isScriptType(script));

            for (let i = 0; i < scripts?.length; i++) {
                const script = scripts[i];

                const scriptText = yield call(scriptDraftsService.getScriptText, script) || script.text;

                const isLargeFile = ((scriptText && scriptText.length) || 0) > CHARACTER_LIMIT;
                const text = isLargeFile
                    ? scriptText.slice(0, TEXT_TO_SHOW_LENGTH)
                    : scriptText;

                const regexp = new RegExp(searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');

                statsScriptsSearch[script.name] = { count: [...(text || '').matchAll(regexp)].length, isLargeFile };
            }
        }

        yield put({ type: types.SCRIPT_SEARCH_SEARCH_TERM_OK, searchKey, searchTerm, statsScriptsSearch });
    } catch (error) {
        yield errorSaga.putError(error);
    }
}

export function* setReplaceTerm({ searchKey, replaceTerm }: ISetReplaceTermParams) {
    try {
        yield put({ type: types.SCRIPT_SEARCH_REPLACE_TERM_OK, searchKey, replaceTerm });
    } catch (error) {
        yield errorSaga.putError(error);
    }
}

export function* setSearchMeta({ searchKey, currentSearchItem, totalSearchResults }: ISetSearchMetaParams) {
    try {
        yield put({ type: types.SCRIPT_SEARCH_SET_META_OK, searchKey, currentSearchItem, totalSearchResults });
    } catch (error) {
        yield errorSaga.putError(error);
    }
}

function sendUpdateToProjectDetailsPanel() {
    var event = new CustomEvent(globalEvents.UPDATE_SCRIPTS_PANEL);
    window.dispatchEvent(event);
}
